import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

import AccountProfile from './components/AccountProfile';
import EmailPreferences from './components/EmailPreferences';
import BillingHistory from './components/BillingHistory';
import CreditManagement from './components/CreditManagement';
import SuccessToast from '../../components/ui/SuccessToast';
import Header from '../../components/navigation/Header';

const Settings = () => {
  const navigate = useNavigate();
  const { user, userProfile, creditBalance, updateProfile, refreshCredits, logout } = useAuth();
  useEffect(() => {
    if (!user?.id) return;
    refreshCredits?.();
  }, [user?.id, refreshCredits]);

  const [activeTab, setActiveTab] = useState('profile');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const tabs = [
    {
      id: 'profile',
      label: 'Account Profile',
      icon: 'User',
      description: 'Manage your personal information'
    },
    {
      id: 'email',
      label: 'Email Preferences',
      icon: 'Mail',
      description: 'Control your notifications'
    },
    {
      id: 'billing',
      label: 'Billing History',
      icon: 'Receipt',
      description: 'View your transactions'
    },
    {
      id: 'credits',
      label: 'Credit Management',
      icon: 'Zap',
      description: 'Manage your credits'
    }
  ];

  const handleSaveSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
  };

  const handleProfileUpdate = async (updatedData) => {
    const { error } = await updateProfile(updatedData);
    if (!error) {
      handleSaveSuccess('Profile updated successfully');
    }
  };

  const handlePurchaseCredits = () => {
    navigate('/purchase-credits');
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleLogout = async () => {
    await logout?.();
    navigate('/login');
  };

  const realUser = {
    name: userProfile?.full_name || user?.user_metadata?.full_name || '',
    email: userProfile?.email || user?.email || '',
    avatar: userProfile?.avatar_url || null,
    creditsRemaining: creditBalance
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <AccountProfile user={realUser} onUpdate={handleProfileUpdate} onSuccess={handleSaveSuccess} />;
      case 'email':
        return <EmailPreferences user={user} onSuccess={handleSaveSuccess} />;
      case 'billing':
        return <BillingHistory userId={user?.id} />;
      case 'credits':
        return <CreditManagement user={realUser} userId={user?.id} onPurchaseCredits={handlePurchaseCredits} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-cyan-50 pb-24">
      <Header
        user={realUser}
        onNavigate={handleNavigate}
        onPurchaseCredits={handlePurchaseCredits}
        onLogout={handleLogout}
      />
      <main className="main-content">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          {/* Header with decorative elements */}
          <div className="text-center mb-6 sm:mb-8 relative">
            {/* Decorative floating icons - hidden on mobile for cleaner look */}
            <div className="hidden sm:block absolute top-0 left-1/4 animate-float">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-cyan-100 flex items-center justify-center">
                <Icon name="Settings" size={20} color="#9333EA" />
              </div>
            </div>
            <div className="hidden sm:block absolute top-0 right-1/4 animate-float-delayed">
              <Icon name="Sparkles" size={24} color="#06B6D4" />
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent mb-2 sm:mb-4">
              Settings
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground px-4">
              Manage your account preferences and billing
            </p>
          </div>

          {/* Tabs Navigation - Mobile Optimized */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6 overflow-hidden">
            <div className="flex overflow-x-auto scrollbar-hide">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 min-w-[120px] sm:min-w-[140px] px-3 sm:px-4 py-4 sm:py-5 flex flex-col items-center gap-2 transition-all border-b-3 touch-manipulation ${
                    activeTab === tab?.id
                      ? 'border-purple-600 bg-gradient-to-br from-purple-50 to-cyan-50' :'border-transparent hover:bg-gray-50 active:bg-gray-100'
                  }`}
                >
                  <Icon
                    name={tab?.icon}
                    size={22}
                    color={activeTab === tab?.id ? '#9333EA' : '#6B7280'}
                  />
                  <div className="text-center">
                    <div
                      className={`text-xs sm:text-sm font-semibold leading-tight ${
                        activeTab === tab?.id
                          ? 'bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent' :'text-gray-700'
                      }`}
                    >
                      {tab?.label}
                    </div>
                    <div className="text-xs text-gray-500 hidden lg:block mt-1">
                      {tab?.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content - Mobile Optimized */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 md:p-8">
            {renderTabContent()}
          </div>
        </div>
      </main>

      <SuccessToast
        isOpen={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage}
      />
    </div>
  );
};

export default Settings;